<template>
	<div class="min-w-0 space-y-3">
		<TemplateSearch
			v-if="!hideSearchWhenSingle || total > 1"
			v-model="search"
			:id="`template-selector-${_uid}`"
			:placeholder="searchPlaceholder"
			:class="selectionCheck ? '[&_input]:h-11' : ''"
		/>
		<p v-if="loading" role="status" class="text-sm text-grey-600">
			{{ $t('general.loading') }}
		</p>
		<div
			v-else-if="error"
			role="alert"
			class="space-y-2 text-sm text-error"
		>
			<p>{{ $t('email-templates.search-error') }}</p>
			<Button type="secondary" @click="load(page)">{{
				$t('application.decline.retry')
			}}</Button>
		</div>
		<p v-else-if="!rows.length" role="status" class="text-sm text-grey-600">
			{{ $t('email-templates.no-results') }}
		</p>
		<div
			v-else
			class="flex max-h-[180px] flex-col gap-1 overflow-y-auto md:max-h-none"
			:aria-label="label || $t('application.decline.choose-template')"
		>
			<button
				v-for="template in rows"
				:key="template.id"
				type="button"
				:disabled="disabled"
				:aria-pressed="String(value === template.id)"
				class="flex w-full items-center justify-between gap-2 rounded-lg p-3 text-left text-sm text-grey-1000 hover:bg-grey-50 focus-visible:ring-2 focus-visible:ring-grey-500"
				:class="[
					value === template.id ? 'bg-grey-100 font-medium' : '',
					selectionCheck ? 'py-2.5' : ''
				]"
				@click="$emit('input', template.id)"
			>
				<span class="truncate" :title="template.name">{{
					template.name
				}}</span>
				<span
					v-if="!selectionCheck && template.is_default"
					class="rounded-full bg-grey-50 px-2 py-1 text-xs"
					>{{ $t('email-templates.default') }}</span
				>
				<IconCheck
					v-if="selectionCheck && value === template.id"
					:size="16"
					class="shrink-0"
				/>
			</button>
		</div>
		<div v-if="lastPage > 1" class="flex justify-between gap-2">
			<Button
				type="secondary"
				:disabled="loading || page <= 1"
				@click="load(page - 1)"
				>{{ $t('crud.previous') }}</Button
			>
			<Button
				type="secondary"
				:disabled="loading || page >= lastPage"
				@click="load(page + 1)"
				>{{ $t('crud.next') }}</Button
			>
		</div>
	</div>
</template>
<script>
import TemplateSearch from './TemplateSearch'
import Button from '~/components/ui/common/Button'
import IconCheck from '~/components/Icons/IconCheck'
export default {
	components: { TemplateSearch, Button, IconCheck },
	props: {
		value: [String, Number],
		searchTemplates: { type: Function, required: true },
		disabled: Boolean,
		hideSearchWhenSingle: Boolean,
		selectionCheck: Boolean,
		searchPlaceholder: String,
		label: String
	},
	data: () => ({
		search: '',
		rows: [],
		page: 1,
		lastPage: 1,
		total: 0,
		loading: true,
		error: false,
		request: 0,
		timer: null
	}),
	watch: {
		search() {
			// Ignore responses for the previous query throughout the debounce window.
			this.request++
			clearTimeout(this.timer)
			this.timer = setTimeout(() => this.load(1), 200)
		}
	},
	mounted() {
		this.load(1)
	},
	beforeDestroy() {
		clearTimeout(this.timer)
		this.request++
	},
	methods: {
		async load(page = 1) {
			const request = ++this.request
			const search = this.search
			this.loading = true
			this.error = false
			try {
				const response = await this.searchTemplates(search, page)
				if (request !== this.request) return
				if (!search)
					this.total = response.meta.total || response.data.length
				this.rows = response.data
				this.page = response.meta.current_page
				this.lastPage = response.meta.last_page
			} catch (error) {
				if (request === this.request) this.error = true
			} finally {
				if (request === this.request) this.loading = false
			}
		}
	}
}
</script>
